import { ChangeEvent, FormEvent, FunctionComponent, useState } from 'react';
import classNames from 'classnames';

import { getErrorsFromRequest } from 'utils/api';

import { setPassword as setPasswordRequest } from 'requests/user';

import IconInvisible from 'theme/icons/invisible.svg';
import IconVisible from 'theme/icons/visible.svg';

import Button from 'components/layout/Button';
import { ButtonIconTypes, ButtonLayouts, ButtonStyles, ButtonTypes } from 'components/layout/Button/types';
import ButtonGoogle from 'components/layout/ButtonGoogle';
import Form from 'components/layout/forms/Form';
import Input from 'components/layout/forms/Input';
import InputRow from 'components/layout/forms/InputRow';
import { InputDecorator } from 'components/layout/forms/InputWrapper/types';
import Image from 'components/layout/Image';

import StyledComponent from './styles';
import { PasswordVisible, Props } from './types';

interface HandleValue {
    [key:string]: string;
}

interface FormState {
    password: string;
    passwordConfirm: string;
    fullName?: string;
    email?: string;
    gCredential?: string;
}

interface Errors extends FormState {
    token: string;
    google: string;
};

const ContentSectionsAcitaveAccountForm: FunctionComponent<Props> = ({ translations, onSuccess, token, viewActivatePatient }) => {
    const [formValues, setFormValues]: [FormState, Function] = useState(null);
    const [errorMessage, setErrorMessage]: [Errors, Function] = useState(null);
    const [passwordVisible, setPasswordVisible]: [PasswordVisible, Function] = useState({ password: false, passwordConfirm: false });

    const bindGoogle = Boolean(formValues?.gCredential);

    const submitForm = (event:FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        setPasswordRequest({ ...formValues, token })
            .then((response) => onSuccess && onSuccess(response))
            .catch((errors) => setErrorMessage(getErrorsFromRequest(errors)));
    };

    const handleChange = (value: HandleValue) => setFormValues((prevState:FormState) => ({ ...prevState, ...value }));

    const getPasswordVisibleButton = (buttonName: string): InputDecorator => ({
        visible: true,
        children: (
            <Button
                className='button-password-visible'
                layout={ButtonLayouts.Block}
                style={ButtonStyles.Secondary}
                onClick={() => setPasswordVisible((prevState: PasswordVisible) => ({
                    ...prevState,
                    [buttonName]: !prevState?.[buttonName],
                }))}
                ariaLabel={ passwordVisible?.[buttonName]
                    ? translations?.buttonPasswordInvisible
                    : translations?.buttonPasswordVisible
                }
                icon={{
                    type: ButtonIconTypes.Element,
                    value: (
                        <div className="button-icon-wrapper">
                            <div className="icon">
                                <Image
                                    src={
                                        passwordVisible?.[buttonName]
                                            ? IconInvisible
                                            : IconVisible
                                    }
                                    layout="fixed"
                                    width="35"
                                    height="30"
                                />
                            </div>
                        </div>
                    ),
                }}
            >
            </Button>
        ),
    }
    );

    return (
        <StyledComponent className="content-section-company-register">
            <Form
                onSubmit={submitForm}
                className="form"
            >
                <InputRow className='form-input-row'>
                    <Input
                        name='password'
                        type={passwordVisible?.password ? 'text': 'password'}
                        label={translations?.inputPassword}
                        placeholder={translations?.inputPassword}
                        onChange={(e:ChangeEvent<HTMLInputElement>) => handleChange({ password: e.target.value })}
                        value={formValues?.password}
                        error={errorMessage?.password}
                        decoratorRight={getPasswordVisibleButton('password')}
                    />
                    <Input
                        name='passwordConfirm'
                        type={passwordVisible?.passwordConfirm ? 'text': 'password'}
                        label={translations?.inputRepeatPassword}
                        placeholder={translations?.inputRepeatPassword}
                        onChange={(e:ChangeEvent<HTMLInputElement>) => handleChange({ passwordConfirm: e.target.value })}
                        value={formValues?.passwordConfirm}
                        error={errorMessage?.passwordConfirm}
                        decoratorRight={getPasswordVisibleButton('passwordConfirm')}
                    />
                </InputRow>
                {viewActivatePatient && (
                    <div className="google">
                        {!bindGoogle && (
                            <ButtonGoogle
                                headline={translations?.googleHeadline}
                                onSuccess={(gCredential, googleUserDetails) => {
                                    setFormValues((prevState: FormState) => ({
                                        ...prevState,
                                        ...googleUserDetails,
                                        gCredential,
                                    }));
                                }}
                                onError={() => alert(translations?.googleError)}
                            />
                        )}
                        {bindGoogle && (
                            <div className="google-binded">
                                <p className="headline">{translations?.googleHeadlineBind}</p>
                                <div className={classNames('details', {
                                    'error': errorMessage?.google,
                                })}
                                >
                                    <Image
                                        className="icon"
                                        src='/icons/google.svg'
                                        layout='fixed'
                                        objectFit='scale-down'
                                        objectPosition="center"
                                        width={28}
                                        height={28}
                                    />
                                    <div className="details-user">
                                        <p className="details-user-name">{formValues?.fullName}</p>
                                        <p className="details-user-email">{formValues?.email}</p>
                                    </div>
                                    <div
                                        className="cancel-icon"
                                        onClick={() => setFormValues((prevState: FormState) => ({
                                            ...prevState,
                                            gCredential: null,
                                        }))}
                                    >
                                        <Image
                                            className="icon"
                                            src='/icons/cancel.svg'
                                            layout='fixed'
                                            objectFit='scale-down'
                                            objectPosition="center"
                                            width={22}
                                            height={22}
                                        />
                                    </div>
                                </div>
                                {errorMessage?.google && (
                                    <p className="google-error">{errorMessage?.google}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
                <Button
                    className="submit-button"
                    type={ButtonTypes.Submit}
                >
                    {translations?.buttonSubmit}
                </Button>
                { errorMessage?.token && (
                    <div className="token-error">
                        {errorMessage?.token}
                    </div>
                )}
            </Form>
        </StyledComponent>
    );
};

export default ContentSectionsAcitaveAccountForm;